import { useState, useCallback, useRef, useEffect } from 'react';
import { initializePayment, verifyPayment } from '../api/payment';
import { apiClient } from '../api/client';
import { TransactionStatus } from '../types';

interface UsePaymentFlowOptions {
  userId: string;
  userName: string;
  userEmail?: string;
  userPhone?: string;
}

interface PaymentFlowState {
  orderId: string;
  paymentId?: string;
  status: 'idle' | 'creating_order' | 'initiating_payment' | 'processing' | 'success' | 'failed';
  error?: string;
  checkoutUrl?: string;
}

export interface PaymentResult {
  orderId: string;
  paymentId: string;
  status: string;
  data?: any;
}

export const usePaymentFlow = (options: UsePaymentFlowOptions) => {
  const [state, setState] = useState<PaymentFlowState>({
    orderId: '',
    status: 'idle',
  });

  const pollingRef = useRef<NodeJS.Timeout | null>(null);

  const cleanup = useCallback(() => {
    if (pollingRef.current) {
      clearInterval(pollingRef.current);
      pollingRef.current = null;
    }
  }, []);

  useEffect(() => {
    return cleanup;
  }, [cleanup]);

  const startPolling = useCallback(async (paymentId: string) => {
    cleanup();

    pollingRef.current = setInterval(async () => {
      try {
        const response = await verifyPayment(paymentId);
        const status = response.data?.status;

        if (status === 'SUCCESS' || status === 'CAPTURED') {
          setState(prev => ({ ...prev, status: 'success' }));
          cleanup();
        } else if (status === 'FAILED') {
          setState(prev => ({ ...prev, status: 'failed', error: 'Payment failed at gateway' }));
          cleanup();
        }
      } catch (err) {
        console.error('Polling error:', err);
      }
    }, 3000);
  }, [cleanup]);

  const createOrderAndPay = useCallback(
    async (amount: number, description: string, locations: { pickup: any, drop: any }, metadata?: Record<string, any>): Promise<PaymentResult> => {
      try {
        setState({ orderId: '', status: 'creating_order' });

        // 1. Create backend order
        const orderNumber = `ORD-${Date.now()}-${Math.random().toString(36).substring(0, 6)}`;
        const { data: order } = await apiClient.post('/orders', {
          orderId: orderNumber,
          userId: options.userId,
          amount,
          pickupLocation: locations.pickup,
          dropLocation: locations.drop,
          metadata
        });

        setState({ orderId: order.orderId, status: 'initiating_payment' });

        // 2. Initiate payment (Unified Orchestration)
        const result: any = await initializePayment({
          amount,
          description,
          userId: options.userId,
          orderId: order.orderId,
          userName: options.userName,
          userEmail: options.userEmail,
          userPhone: options.userPhone,
          metadata: { ...metadata, method: metadata?.method || 'UPI' }
        });

        if (result.redirect) {
          return { orderId: order.orderId, paymentId: '', status: 'redirected' };
        }

        setState(prev => ({
          ...prev,
          paymentId: result.paymentId,
          status: result.success ? 'success' : 'processing',
        }));

        if (!result.success) {
          startPolling(result.paymentId);
        }

        return {
          orderId: order.orderId,
          paymentId: result.paymentId,
          status: result.success ? 'success' : 'processing',
          data: result.data
        };

      } catch (error: any) {
        console.error('Payment flow error:', error);
        setState(prev => ({
          ...prev,
          status: 'failed',
          error: error.message || 'Payment failed'
        }));
        throw error;
      }
    },
    [options, startPolling]
  );

  const reset = useCallback(() => {
    cleanup();
    setState({ orderId: '', status: 'idle' });
  }, [cleanup]);

  return {
    ...state,
    createOrderAndPay,
    reset,
  };
};
